import { Application } from '../application/application';
import { Observable, Subscription, fromEvent, Subscriber } from 'rxjs';
import { map, concatAll, takeUntil, filter, pairwise, startWith } from 'rxjs/operators';
import { eventOptionsSupported, delegatedObserver } from '../dom/dom';
import { xhrPromise, Method, ResponseType, XHRPromiseOptions } from '../xhr/xhr';
import { throttle } from '../util/util';
import { getSouthWest, getNorthEast } from '../map/map';
import { closeNumber } from '../geometry/geometry';
import { DataSource } from '../data-source/source';
import { DataList } from '../data-source/data-list';
import { SpatialBounds, TemporalRange } from '../data-source/types';
import * as util from '../data-source/util';
import { ContainerPanel } from './panel';

export const DEFAULT_DRAWER_WIDTH = 475;

export const REQUEST_ATTRIB_DOMAIN = "${domain}";
export const REQUEST_ATTRIB_CONTAINER = "${container}";

export interface PanelState {
  cabinets?: {
    name: string,
    drawers: {
      name: string,
      width?: number
    }[],
    width?: number
  }[],
  height?: number
}

export interface ContainerDefinition extends PanelState {
  // method that can construct (rather than load) the markup from source
  build?: (panel: ContainerPanel, containerId: string, application: Application) => Container,
  // url where to load (rather than build) the markup from
  url?: string,
  onOpen?: (container: Container) => void,
  onDestroy?: (container: Container) => void,
  canClose?: (container: Container) => boolean
}


export interface DrawerSizeMessage {
  drawer: Drawer,
  width: number,
  height: number
}

export class Drawer {
  private container: HTMLDivElement;
  private sectionContainer: HTMLDivElement;
  private style: CSSStyleDeclaration;
  private enabled: boolean;
  private resizeSubscriptions: Subscription[];
  private lastTouchPagePosition: { identifier: number, pageX: number, pageY: number };

  private sizeObservable: Observable<DrawerSizeMessage>;
  private sizeSubscribers: Subscriber<DrawerSizeMessage>[];

  constructor(private name: string, private cabinet: Cabinet, zIndex: number, drawerDiv?: HTMLDivElement) {
    if (!drawerDiv) {
      cabinet.getContainerNode().insertAdjacentHTML(
        'beforeend',
        `<div class="tlmd-panel ${name} ${cabinet.getName()}${name} tlmd-noresize" data-name="${name}" data-cab="${cabinet.getName()}" style="z-index:${zIndex};">
                    <div class="tlmd-panel-flexcontainer"></div>
                    <div class="tlmd-resizer tlmd-west"></div>
                    <div class="tlmd-resizer tlmd-south"></div>
                    <div class="tlmd-resizer tlmd-southwest"></div>
                  </div>`);
    } else {
      drawerDiv.classList.add('tlmd-panel', name, `${cabinet.getName()}${name}`, 'tlmd-noresize');
      drawerDiv.dataset.name = name;
      drawerDiv.dataset.cab = cabinet.getName();
      drawerDiv.style.zIndex = zIndex.toString();
      drawerDiv.insertAdjacentHTML(
        'beforeend',
        `<div class="tlmd-resizer tlmd-west"></div>
                    <div class="tlmd-resizer tlmd-south"></div>
                    <div class="tlmd-resizer tlmd-southwest"></div>`
      );
    }





    this.container = <HTMLDivElement>cabinet.getContainerNode().querySelector(`div.tlmd-panel.${name}`);
    this.sectionContainer = <HTMLDivElement>this.container.querySelector('div.tlmd-panel-flexcontainer');
    this.style = window.getComputedStyle(this.container);
    this.container.style.width = '0';
    this.container.style.marginLeft = '0';
    this.enabled = true;
    this.lastTouchPagePosition = null;

    let resizerS = <HTMLDivElement>this.container.querySelectorAll('div.tlmd-resizer.tlmd-south')[0];
    let resizerW = <HTMLDivElement>this.container.querySelectorAll('div.tlmd-resizer.tlmd-west')[0];
    let resizerSW = <HTMLDivElement>this.container.querySelectorAll('div.tlmd-resizer.tlmd-southwest')[0];

    const mouseEventToCoordinate = ((event: MouseEvent) => { return { pageX: event.pageX, pageY: event.pageY }; });
    const mouseCoordinateToDeltaSouthWest = ((mousePosition: { pageX: number, pageY: number }) => { return { dWest: (this.getWest() - mousePosition.pageX), dSouth: (mousePosition.pageY - this.getSouth()) }; });

    const resizeWest: (dSouthWest: { dSouth: number, dWest: number }) => void = (dSouthWest) => this.cabinet.slideDrawer(this.name, dSouthWest.dWest);
    const resizeSouth: (dSouthWest: { dSouth: number, dWest: number }) => void = (dSouthWest) => this.cabinet.adjustHeight(dSouthWest.dSouth);


    let resizeSObs = fromEvent(resizerS, 'mousedown', eventOptionsSupported ? { capture: false, passive: false, once: false } : undefined)//these options are all the default value but added here for reference
      .pipe(
        map(event => {
          event.preventDefault();//prevents unwanted text selection 
          return this.getCabinet().getContainer().getPanel().getMovesUntilMouseUpObserver()
            .pipe(
              map(mouseEventToCoordinate),
              map(mouseCoordinateToDeltaSouthWest)
            );
        }),
        concatAll()
      );
    let resizeWObs = fromEvent(resizerW, 'mousedown').pipe(map(event => { event.preventDefault(); return this.getCabinet().getContainer().getPanel().getMovesUntilMouseUpObserver().pipe(map(mouseEventToCoordinate), map(mouseCoordinateToDeltaSouthWest)); }), concatAll());
    let resizeSWObs = fromEvent(resizerSW, 'mousedown').pipe(map(event => { event.preventDefault(); return this.getCabinet().getContainer().getPanel().getMovesUntilMouseUpObserver().pipe(map(mouseEventToCoordinate), map(mouseCoordinateToDeltaSouthWest)); }), concatAll());


    this.resizeSubscriptions = [
      resizeSObs.subscribe(resizeSouth),
      resizeWObs.subscribe(resizeWest),
      resizeSWObs.subscribe((dSouthWest: { dSouth: number, dWest: number }) => { resizeSouth(dSouthWest); resizeWest(dSouthWest); })];

    const eventToTouch = (event: TouchEvent, identifier: number): Touch => Array.from(event.changedTouches).filter(touch => touch.identifier == identifier).reduce((firstTouch, touch) => firstTouch ? firstTouch : touch, undefined);





    const touchPairToCoordinateAndDelta = (touchPair: Touch[]): { identifier: number, pageX: number, pageY: number, dWest: number, dSouth: number } => {
      return {
        identifier: touchPair[0].identifier,
        pageX: (touchPair[0].pageX),
        pageY: (touchPair[0].pageY),
        dWest: touchPair[0].pageX - touchPair[1].pageX,
        dSouth: touchPair[1].pageY - touchPair[0].pageY
      }
    };
    const touchingTheSouth = (position: { pageX: number, pageY: number }): boolean => this.getSouth() - position.pageY < 100;
    const touchingTheWest = (position: { pageX: number, pageY: number }): boolean => position.pageX - this.getWest() < 100;


    //      let touchStartWObs2 = <Observable<{ identifier:number, pageX:number, pageY:number }>>fromEvent(this.container,'touchstart',eventOptionsSupported ? {passive:true} : undefined);
    //      .pipe(
    //              filter( (event:TouchEvent) => event.touches.length == 1),
    //              map(touchEventToCoordinate));


    let touchStart = <Observable<TouchEvent>>fromEvent(this.container, 'touchstart', eventOptionsSupported ? { passive: true } : undefined);
    let touchMove = <Observable<TouchEvent>>fromEvent(this.container, 'touchmove', eventOptionsSupported ? { passive: false } : undefined);
    let touchEnd = this.getCabinet().getContainer().getPanel().getTouchEndObserver();

    let touchDrags = touchStart
      .pipe(
        filter(startEvt => startEvt.touches.length == 1),
        map(startEvt => touchMove.
          pipe(
            takeUntil(touchEnd
              .pipe(
                filter(endEvt => Array.from(endEvt.changedTouches).filter(touch => touch.identifier == startEvt.changedTouches[0].identifier).length == 1),
              )
            ),
            filter(moveEvt => Array.from(moveEvt.changedTouches).filter(touch => touch.identifier == startEvt.changedTouches[0].identifier).length == 1),
            startWith(startEvt),
            map(moveEvt => eventToTouch(moveEvt, startEvt.changedTouches[0].identifier)),
            pairwise(),
            map(touchPairToCoordinateAndDelta)
          )
        ),
        concatAll()
      );

    this.resizeSubscriptions.push(

      touchDrags.subscribe(coord => {
        if (touchingTheSouth(coord)) {
          this.cabinet.adjustHeight(coord.dSouth);
          if (touchingTheWest(coord)) {
            this.cabinet.slideDrawer(this.name, coord.dWest);
          }
        } else {
          this.cabinet.slideDrawer(this.name, coord.dWest);
        }
      })
    );
    this.sizeSubscribers = [];
    //we create an observable of our own, one that sends resize notifications
    this.sizeObservable = new Observable<DrawerSizeMessage>(subscriber => {
      this.sizeSubscribers.push(subscriber);

      //return the unsubscribe function
      return () => {
        let index = this.sizeSubscribers.indexOf(subscriber);
        if (index != -1) {
          this.sizeSubscribers.splice(index, 1);
        }
      }
    })

  }

  public release() {
    this.resizeSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.resizeSubscriptions.length = 0;
    //emit complete to subscribers
    this.sizeSubscribers.forEach(subscriber => {
      subscriber.complete();
    });
    this.sizeSubscribers.length = 0;
  }
  public getCabinet(): Cabinet {
    return this.cabinet;
  }
  public getContainer(): Container {
    return this.cabinet.getContainer();
  }
  public getName(): string {
    return this.name;
  }
  public getContainerNode(): HTMLDivElement {
    return this.container;
  }
  public getSelector(relative = true): string {
    return `${this.cabinet.getSelector(relative)} div.tlmd-panel.${this.name}`;
  }
  public getStyle(): CSSStyleDeclaration {
    return this.style;
  }
  public isEnabled(): boolean {
    return this.enabled;
  }
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  public getContent(): HTMLDivElement {
    return this.sectionContainer;
  }
  public clearContent(beforeRemove?: (parent: HTMLDivElement) => void): HTMLDivElement {
    if (beforeRemove) {
      beforeRemove(this.sectionContainer);
    }
    while (this.sectionContainer.firstChild) {
      this.sectionContainer.removeChild(this.sectionContainer.firstChild);
    }
    return this.sectionContainer;
  }
  public setTextSelection(enable: boolean) {
    if (enable) {
      this.getContent().classList.remove('tlmd-noselect');
    } else {
      this.getContent().classList.add('tlmd-noselect');
    }
  }
  public setResizable(enable: boolean) {
    if (enable) {
      this.getContainerNode().classList.remove('tlmd-noresize');
    } else {
      this.getContainerNode().classList.add('tlmd-noresize');
    }
  }
  public setWidth(width: number, updateCabinet = true): number {
    this.container.style.width = `${width}px`;
    this.container.style.marginLeft = `${-width}px`;
    if (updateCabinet) this.cabinet.updateWidth();

    //emit the new size
    this.sizeSubscribers.forEach(subscriber => {
      subscriber.next({ drawer: this, width: width, height: this.getHeight() });
    });

    return width;
  }
  public getWidth(): number {
    return parseFloat(this.style.width);
  }
  public isOpen(): boolean {
    return parseFloat(this.style.width) > 0;
  }
  public getWest(): number {
    return this.getEast() - this.getWidth();
  }
  public getEast(): number {
    return this.cabinet.getEast();
  }
  public getNorth(): number {
    return this.cabinet.getNorth();
  }
  public getSouth(): number {
    return this.cabinet.getSouth();
  }
  public getHeight(): number {
    return this.cabinet.getHeight();
  }
  public getSizeObservable(): Observable<DrawerSizeMessage> {
    return this.sizeObservable;
  }
}

export class Cabinet {
  private containerElem: HTMLDivElement;
  private style: CSSStyleDeclaration;
  private drawers: Map<string, Drawer>;

  constructor(private name: string, private container: Container, cabinetDiv?: HTMLDivElement) {
    if (!cabinetDiv) {
      container.getContainerNode().insertAdjacentHTML('beforeend', `<div class="tlmd-cabinet  ${name}"></div>`);
    } else {
      cabinetDiv.classList.add('tlmd-cabinet', name);
      container.getContainerNode().appendChild(cabinetDiv);
    }
    this.containerElem = <HTMLDivElement>container.getContainerNode().querySelector(`div.tlmd-cabinet.${name}`);
    this.style = window.getComputedStyle(this.containerElem);
    this.drawers = new Map();
  }

  public release() {
    Array.from(this.drawers.values()).forEach(drawer => drawer.release());
  }

  public getContainer(): Container {
    return this.container;
  }
  public getName(): string {
    return this.name;
  }
  public getContainerNode(): HTMLDivElement {
    return this.containerElem;
  }
  public getSelector(relative = true): string {
    if (relative) {
      return `div.tlmd-cabinet.${this.name}`;
    } else {
      return `${this.container.getContainerNodeSelector()} div.tlmd-cabinet.${this.name}`;
    }
  }
  public getDrawer(drawer?: string | number): Drawer {
    if ((<string>drawer).toLowerCase) {
      return this.drawers.get(<string>drawer);
    } else {
      if (<number>drawer > -1) {
        return Array.from(this.drawers.values())[<number>drawer];
      } else {
        return undefined;
      }
    }
  }
  public isOpen(drawerName: string): boolean {
    let drawer = this.getDrawer(drawerName);
    if (drawer) return drawer.isOpen();
    return false;
  }
  public getDrawers(): Map<string, Drawer> {
    return this.drawers;
  }
  public addDrawer(drawerName: string | string[], drawerDiv?: HTMLDivElement | HTMLDivElement[]): Drawer | Map<string, Drawer> {
    let zIndex = this.drawers.size * 10;
    if ((<String>drawerName).toUpperCase) {//a single drawer
      this.drawers.set(<string>drawerName, new Drawer(<string>drawerName, this, zIndex + 10, <HTMLDivElement>drawerDiv));
      if (this.drawers.size > 1) {
        this.containerElem.classList.add('tlmd-overlap');
      }
      return this.drawers.get(<string>drawerName);
    } else {//a list of drawers to add
      let addedDrawers = new Map<string, Drawer>();
      (<Array<string>>drawerName).forEach((name, index) => {
        this.drawers.set(name, new Drawer(name, this, zIndex + ((index + 1) * 10), drawerDiv ? (<HTMLDivElement[]>drawerDiv)[index] : undefined));
        addedDrawers.set(name, this.drawers.get(name))
      });
      this.containerElem.classList.add('tlmd-overlap');
      return addedDrawers;
    }
  }
  public updateWidth() {
    if (this.drawers.size > 1) {
      let width = Array.from(this.drawers.values()).reduce((maxWidth, drawer) => Math.max(maxWidth, drawer.getWidth()), 0);
      this.containerElem.style.width = `${width}px`;
    }
  }
  public getWidth(): number {
    return parseFloat(this.style.width);
  }
  public setWidth(width: number): void {
    this.containerElem.style.width = `${width}px`;
  }
  public getWest(): number {
    return this.getEast() - this.getWidth();
  }
  public getEast(): number {
    return this.getContainer().getCabinetEast(this.name);
  }
  public getNorth(): number {
    return this.getContainer().getPanel().getNorth();
  }
  public getSouth(): number {
    return this.getContainer().getPanel().getSouth();
  }
  public getHeight(): number {
    return this.getContainer().getPanel().getHeight();
  }
  public getDrawerWidths(): number[] {
    return Array.from(this.drawers.values())
      .map(drawer => drawer.getWidth());
  }
  public shutDrawer(drawerName: string): Drawer {
    return this.getContainer().shutDrawer(this.name, drawerName);
  }
  public slideDrawer(drawerName: string, delta: number): Drawer {
    return this.getContainer().slideDrawer(delta, this.name, drawerName);
  };
  public adjustHeight(delta: number): void {
    this.getContainer().adjustHeight(delta);
  }

  /**
  * Returns the number of open drawers. If a drawer name is given
  * only the drawers until the drawer with given name are counted.
  */
  public openCount(drawerName?: string): number {
    let drawerIndex = drawerName ? this.getDrawerIndex(drawerName) : this.drawers.size;
    return Array.from(this.drawers.values())
      .filter((drawer, index) => index < drawerIndex && drawer.getWidth() > 0)
      .length;
  }
  public getDrawerIndex(drawerName: string): number {
    return Array.from(this.drawers.keys()).indexOf(drawerName);
  }
}
/**
 * A container manages a set of  cabinets. Each Cabinet has a set
 * of Drawers. It's main job is to load/build the markup and attach
 * or release all handlers necessary.
 */
export class Container {
  private subscriptions: Subscription[] = [];
  private containerSubscriptions: Subscription[] = [];
  private definition: ContainerDefinition = undefined;
  private cabinets: Map<string, Cabinet>;
  private drawerIdentyfiers: Map<string, { cabinet: string, drawer: string }>;

  //for quick access to the width of the document body
  private documentStyle: CSSStyleDeclaration;

  constructor(private panel: ContainerPanel, private containerId: string, private app: Application) {
    this.cabinets = new Map();
    this.drawerIdentyfiers = new Map();

    this.documentStyle = window.getComputedStyle(document.body);
  }

  public getApplication(): Application {
    return this.app;
  }

  public getPanel(): ContainerPanel {
    return this.panel;
  }

  /**
   *Gets called by the ContainerPanel immediately after construction of the Container object 
   */
  public setup(definition: ContainerDefinition): Container {
    this.definition = definition;
    return this;
  }

  public open(): Promise<Container> {
    return new Promise<Container>((resolve, reject) => {

      if (this.definition) {
        this.buildContainer()
          .then(container => {
            //let external scripts run their thing once the skeleton markup became available
            if (container.definition && container.definition.onOpen) {
              container.definition.onOpen(container);
            }
            resolve(container);
          }).catch(container => {
            //??
            reject(container);
          });
      } else {
        reject(this);
      }
    })
  }

  public getContainerNodeSelector(): string {
    return `${this.getPanel().getContainerNodeSelector(this.containerId)}`;
  }

  public getContainerNode(): HTMLDivElement {
    return (<HTMLDivElement>document.querySelector(this.getContainerNodeSelector()));
  }

/**
 *Override this method if you want to react on a container that has been closed
 */
  public containerClosed( containerId: string): void {
  }
/**
 *Override this method if you want to react on a dialog that has been closed
 */
  public dialogClosed( dialogId: string): void {
  }

  public close(): boolean {
    if (this.canClose()) {
      this.release();
      return true;
    } else {
      return false;
    }
  }

  private canClose(): boolean {
    if (this.definition && this.definition.canClose) {
      return this.definition.canClose(this);
    }
    return true;
  }

  public destroy(): Container {
    if (this.containerSubscriptions && this.containerSubscriptions.length > 0) {
      this.containerSubscriptions.forEach(subscription => subscription.unsubscribe());
      this.containerSubscriptions.length = 0;
    }
    this.release();
    let elem = this.getContainerNode();
    elem.parentNode.removeChild(elem);
    return this;
  }

  public release(): Container {
    //console.trace('releasing container %s', this.containerId);
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions.length = 0;
    }
    if (this.definition && this.definition.onDestroy) {
      this.definition.onDestroy(this);
    }
    Array.from(this.cabinets.values()).forEach(cabinet => cabinet.release());
    this.cabinets.clear();
    this.drawerIdentyfiers.clear();
    this.getContainerNode().innerHTML = '';
    return this;
  }

  /**
   * Call this method to add subscriptions to content dom elements.
   * These elements get removed upon each call to Open) -> buildContainer()
   * so by registering the subscriptions, they get properly unsubscribed.
   */
  public addSubscription(subscription: Subscription): Container {
    this.subscriptions.push(subscription);
    return this;
  }
  /**
   * Call this method in order to add delegated subscriptions 
   * to the container. These subscriptions will never be removed
   * for as long as the container is present.
   */
  protected addContainerDelegatedSubscription(event: string, selector: string, eventHandler: (evAndTrgt: { event: Event, target: HTMLElement }) => void, eventOptions?: EventListenerOptions): Observable<{ event: Event, target: HTMLElement }> {
    let observable = delegatedObserver(this.getContainerNode(), event, selector, eventOptions);
    this.containerSubscriptions.push(observable.subscribe(eventHandler));
    return observable;
  }

  protected buildContainer(): Promise<Container> {
    //to be sure if this happens to be an accidental reload
    this.release();

    return new Promise<Container>((resolve, reject) => {
      if (!this.definition.url) {
        //No url so build dynamically when cabinet definitions are present
        this.definition = <ContainerDefinition>this.fixState(this.definition, true);
        this.definition.cabinets.forEach(cabinetState => {
          this.addCabinet(cabinetState.name).addDrawer(cabinetState.drawers.map(drawerState => {
            this.drawerIdentyfiers.set(`${cabinetState.name}${drawerState.name}`, { cabinet: cabinetState.name, drawer: drawerState.name });
            return drawerState.name;
          }));
        });
        this.definition.cabinets.forEach(cabinetState => {
          cabinetState.drawers.forEach(drawerState => this.slideDrawer(0, cabinetState.name, drawerState.name));
        });


        resolve(this);
      } else {
        //a url has been defined so load the markup
        xhrPromise(
          {
            url: this.definition.url.replace(REQUEST_ATTRIB_DOMAIN, this.getApplication().getDomain()).replace(REQUEST_ATTRIB_CONTAINER, this.containerId),
            method: Method.get,
            accept: 'text/html',
            responseType: ResponseType.document,
            digester: this.getApplication().getDigester()
          }
        ).then((response) => {

          let containerDiv = <HTMLDivElement>response.data;

          this.definition.cabinets = this.definition.cabinets ? this.definition.cabinets : [];
          this.definition = <ContainerDefinition>this.fixState(this.definition, true);

          Array.from(containerDiv.children).forEach((cabinetDiv: HTMLDivElement, cabIndex: number) => {
            let cabinetName = cabIndex < this.definition.cabinets.length ? this.definition.cabinets[cabIndex].name : `cab${cabIndex}`;
            let cabinet = this.addCabinet(cabinetName, cabinetDiv);

            if (cabIndex >= this.definition.cabinets.length) {
              this.definition.cabinets.push({ name: cabinetName, drawers: [], width: 0 });
            }

            let drawerDivs = <HTMLDivElement[]>Array.from(cabinetDiv.children);
            let drawerNames = drawerDivs.map((drawerDiv: HTMLDivElement, drawerIndex: number) => {

              let drawerName = drawerIndex < this.definition.cabinets[cabIndex].drawers.length ? this.definition.cabinets[cabIndex].drawers[drawerIndex].name : `drw${drawerIndex}`;
              if (drawerIndex >= this.definition.cabinets[cabIndex].drawers.length) {
                this.definition.cabinets[cabIndex].drawers.push({ name: drawerName, width: 0 });
              }
              this.drawerIdentyfiers.set(`${cabinetName}${drawerName}`, { cabinet: cabinetName, drawer: drawerName });
              return drawerName;
            });
            cabinet.addDrawer(drawerNames, drawerDivs);

          });

          this.definition.cabinets.forEach(cabinetState => {
            cabinetState.drawers.forEach(drawerState => this.slideDrawer(0, cabinetState.name, drawerState.name));
          });

          resolve(this);

        }).catch((error) => {
          console.error(error);
          reject(this);
        });
      }
    }
    ).then(container => {
      return container.finishContainerBuild();
    });
  }
  /**
   * Override when sub classes need to alter the XHR calll for loading the content 
   */
  protected beforeContentLoad(promiseOptions: XHRPromiseOptions): XHRPromiseOptions {
    return promiseOptions
  }
  /**
   * Override when sub classes need extra work before the onLoad can be called 
   */
  protected finishContainerBuild(): Promise<Container> {
    return new Promise<Container>((resolve, _reject) => resolve(this));
  }

  public getState(): PanelState {
    return this.definition;
  }
  public setState(state: PanelState): void {
    this.definition.cabinets = this.fixState(state, false).cabinets; //only set drawer widths. No height processing
    window.requestAnimationFrame((time) => this.draw());
  }

  private fixState(state: PanelState, processHeight = true): PanelState {
    if (!state) {
      return { cabinets: [], height: 0 };
    } else {
      return Object.assign({}, state, {
        cabinets: state.cabinets
          .map(cab => {
            return {
              name: cab.name,
              drawers: cab.drawers
                .map(drawer => {
                  return {
                    name: drawer.name,
                    width: (drawer.width != null && drawer.width > 0 ? drawer.width : 0)
                  };
                }),
              width: Math.max(...cab.drawers.map(drawer => (drawer.width && drawer.width > 0 ? drawer.width : 0)))
            };
          }),
        height: processHeight ? state.height != null && state.height > 0 ? state.height : this.getPanel().getHeight() : state.height
      });
    }
  }

  private addCabinet(cabinetName: string, cabinetDiv?: HTMLDivElement): Cabinet {
    this.cabinets.set(cabinetName, new Cabinet(cabinetName, this, cabinetDiv));
    return this.cabinets.get(cabinetName);
  }

  public getCabinet(cabinetName: string): Cabinet {
    return this.cabinets.get(cabinetName);
  }

  public getDrawer(cabinet: string | number, drawer?: string | number): Drawer {
    let indices = this.getDrawerIndices(cabinet, drawer);
    let cabinetIndex = indices.cabinet;
    let drawerIndex = indices.drawer;
    if (cabinetIndex > -1 && drawerIndex > -1) {
      return Array.from(this.cabinets.values())[cabinetIndex].getDrawer(drawerIndex);
    } else {
      return undefined;
    }
  }

  //called by cabinets
  public getCabinetEast(cabinet: string | number): number {
    let cabinetIndex = (<string>cabinet).toLowerCase ? this.getCabinetIndex(<string>cabinet) : <number>cabinet;
    let right = Array.from(this.cabinets.values())
      .filter((cabinet, index) => index < cabinetIndex)
      .map(cabinet => cabinet.getWidth())
      .reduce((sum, width) => sum + width, 0);

    return parseFloat(this.documentStyle.width) - right;
  }

  public adjustHeight(delta: number): void {
    this.definition.height += delta;
    //draw
    window.requestAnimationFrame((time) => this.draw());
  }

  private getDrawerIdentifier(cabinetName: string, drawerName?: string): { cabinet: string, drawer: string } {
    return drawerName ? this.drawerIdentyfiers.get(cabinetName + drawerName) : this.drawerIdentyfiers.get(cabinetName);
  }

  private getCabinetIndex(cabinetName: string): number {
    return this.definition.cabinets
      .map((cabinetState, cabinetIndex) => { return { state: cabinetState, index: cabinetIndex } })
      .filter(stateAndIndex => stateAndIndex.state.name == cabinetName)
      .reduce((index, stateAndIndex) => index == -1 ? stateAndIndex.index : index, -1);
  }
  private getDrawerIndex(cabinetIndex: number, drawerName: string): number {
    return this.definition.cabinets[cabinetIndex].drawers
      .map((drawerState, drawerIndex) => { return { state: drawerState, index: drawerIndex } })
      .filter(stateAndIndex => stateAndIndex.state.name == drawerName)
      .reduce((index, stateAndIndex) => index == -1 ? stateAndIndex.index : index, -1);
  }

  public shutDrawer(cabinet: string | number, drawer?: string | number): Drawer {
    let indices = this.getDrawerIndices(cabinet, drawer);
    let cabinetIndex = indices.cabinet;
    let drawerIndex = indices.drawer;

    if (cabinetIndex > -1) {
      if (drawerIndex > -1) {
        this.definition.cabinets[cabinetIndex].drawers[drawerIndex].width = 0;
        this.definition.cabinets[cabinetIndex].width = Math.max(...this.definition.cabinets[cabinetIndex].drawers.map(drawerState => drawerState.width));
        //draw
        window.requestAnimationFrame((time) => this.draw());
        return this.getDrawer(cabinetIndex, drawerIndex);
      }
    }
    return undefined
  }

  /**
   * Returns the number of open drawers. If a drawer index is given
   * only the drawers until the drawer with given name are counted.
   */
  private openDrawerCount(cabinetIndex: number, drawerIndex?: number): number {
    drawerIndex = drawerIndex != null ? drawerIndex : this.definition.cabinets[cabinetIndex].drawers.length;
    return this.definition.cabinets[cabinetIndex].drawers
      .filter((drawer, index) => index < drawerIndex && drawer.width > 0)
      .length;
  }

  private getMaxDrawerWidth(cabinetIndex: number, drawerIndex: number) {
    let bumperCount = this.definition.cabinets
      .map((cabinetState, cabIndex) => this.openDrawerCount(cabIndex, cabIndex == cabinetIndex ? drawerIndex : undefined))
      .reduce((totalCount, count) => totalCount + count, 0);
    return this.getPanel().getMaxWidth() - bumperCount * this.getPanel().options.bumperWidth;
  }

  private getDrawerIndices(cabinet: string | number, drawer?: string | number): { cabinet: number, drawer: number } {
    let cabinetName = drawer !== undefined ? (<string>cabinet).toLocaleLowerCase ? (<string>cabinet) : undefined : undefined;
    let cabinetIndex = (<string>cabinet).toLocaleLowerCase ? undefined : <number>cabinet;
    let drawerName = drawer !== undefined ? (<string>drawer).toLocaleLowerCase ? (<string>drawer) : undefined : undefined;
    let drawerIndex = drawer !== undefined ? (<string>drawer).toLocaleLowerCase ? undefined : <number>drawer : undefined;
    if (cabinetIndex === undefined) {
      if (cabinetName !== undefined) {
        cabinetIndex = this.getCabinetIndex(cabinetName);
      } else {
        let id = this.getDrawerIdentifier(<string>cabinet);
        cabinetIndex = this.getCabinetIndex(id.cabinet);
        drawerName = id.drawer;
      }
    }
    if (drawerIndex === undefined) {

      drawerIndex = cabinetIndex >= 0 ? this.getDrawerIndex(cabinetIndex, drawerName) : -1;
    }
    return { cabinet: cabinetIndex, drawer: drawerIndex };
  }
  /**
   * The work horse that tries to honour the drawer slide as much as possible, sliding other drawers when necessary
   * @param cabinetName
   * @param drawerName
   * @param delta
   */
  public slideDrawer(delta: number, cabinet: string | number, drawer?: string | number): Drawer {
    let indices = this.getDrawerIndices(cabinet, drawer);
    let cabinetIndex = indices.cabinet;
    let drawerIndex = indices.drawer;

    if (cabinetIndex > -1) {
      if (drawerIndex > -1) {
        let bumperWidth = this.getPanel().options.bumperWidth;
        let drawerWidth = this.definition.cabinets[cabinetIndex].drawers[drawerIndex].width;

        let maximumDrawerWidth = this.getMaxDrawerWidth(cabinetIndex, drawerIndex);
        let minimumDrawerWidth: number = this.definition.cabinets[cabinetIndex].drawers
          .filter((drawerState, index) => index > drawerIndex && drawerState.width > 0)
          .length * bumperWidth + bumperWidth;

        if (drawerWidth > 0 || delta > 0) {
          drawerWidth += delta;
          drawerWidth = Math.max(drawerWidth, minimumDrawerWidth);
          drawerWidth = Math.min(drawerWidth, maximumDrawerWidth);
        }

        //now figure out everybody else's width
        //first get the widths of the drawers preceding this drawer in our cabinet plus the width of the drawer itself
        let successiveDrawerWidths = this.definition.cabinets[cabinetIndex].drawers
          .filter((drawerState, index) => index < drawerIndex)
          .reverse()
          .reduce((widths, drawerState) => {
            let dw = drawerState.width;
            if (dw > 0 && dw < (widths[0] + bumperWidth)) {//drawer is open and not opened far enough
              return [widths[0] + bumperWidth].concat(widths);
            } else {
              return [dw].concat(widths);
            }
          }, [drawerWidth]);

        //second add the widths of the drawers that succeed this drawer in our cabinet
        let drawerWidths = this.definition.cabinets[cabinetIndex].drawers
          .filter((drawerState, index) => index > drawerIndex)
          .reduce((widths, drawerState) => {
            let dw = drawerState.width;
            if (dw > 0 && dw > (widths[widths.length - 1] - bumperWidth)) {//drawer is open but too far
              return widths.concat([widths[widths.length - 1] - bumperWidth]);
            } else {
              return widths.concat([dw]);
            }
          }, successiveDrawerWidths);
        let cabinetWidth = Math.max(...drawerWidths);

        //how much space do the successive cabinets take?
        let successiveCabinetDrawerWidths =
          this.definition.cabinets
            .filter((cabinetState, index) => index > cabinetIndex)
            .map(cabinetState => cabinetState.drawers.map(drawerState => drawerState.width));
        let successiveCabinetWidths = successiveCabinetDrawerWidths
          .map(drawerWidths => Math.max(...drawerWidths));
        let successiveWidth = successiveCabinetWidths.reduce((sum, width) => sum + width, 0);

        //how much space do the preceding cabinets take?
        let precedingCabinetDrawerWidths =
          this.definition.cabinets
            .filter((cabinetState, index) => index < cabinetIndex)
            .map(cabinetState => cabinetState.drawers.map(drawerState => drawerState.width));
        let precedingCabinetWidths = precedingCabinetDrawerWidths
          .map(drawerWidths => Math.max(...drawerWidths));
        let precedingWidth = precedingCabinetWidths.reduce((sum, width) => sum + width, 0);

        let totalAvailableWidth = this.getPanel().getMaxWidth();

        //subtract the space this cabinet needs
        totalAvailableWidth -= cabinetWidth;
        if (precedingWidth + successiveWidth > totalAvailableWidth) {//we need to close other drawers
          //define this helper function
          let slideCabinetDrawers = (drawerWidths: number[], delta: number, cabinetMinimumWidth?: number): number[] => {
            if (delta > 0) {
              return drawerWidths.map(width => width + delta);
            } else {
              let cabinetReduction = -delta;
              let cabinetWidth = Math.max(...drawerWidths);
              cabinetReduction = Math.min(cabinetReduction, cabinetWidth - cabinetMinimumWidth);
              let reducedDrawerWidths = drawerWidths
                .reverse()
                .reduce((accu, width) => {
                  if (width > 0) {
                    accu.widths.push(Math.max(width - cabinetReduction, (accu.openIndex != -1 ? accu.widths[accu.openIndex] : 0) + bumperWidth));
                    accu.openIndex = accu.widths.length - 1;
                  } else {
                    accu.widths.push(0);
                  }
                  return accu;
                }, { openIndex: -1, widths: [] })
                .widths.reverse();
              return reducedDrawerWidths;

            }
          };

          //try successive cabinets first
          //what is the minimum width the successive cabinets require
          let successiveMinimumCabinetWidths = this.definition.cabinets
            .map((cabinetState, index) => { return { state: cabinetState, index: index }; })
            .filter((cabinetStateAndIndex) => cabinetStateAndIndex.index > cabinetIndex)
            .map(cabinetStateAndIndex => this.openDrawerCount(cabinetStateAndIndex.index) * bumperWidth);
          let successiveMinimumWidth = successiveMinimumCabinetWidths.reduce((sum, width) => sum + width, 0);

          if (precedingWidth + successiveMinimumWidth > totalAvailableWidth) {//we need to close preceding cabinet drawers too
            //close all drawers in the successive cabinets as much as we can (bumperWidth is the minimum allowed opening, unless already closed)
            successiveCabinetDrawerWidths = successiveCabinetDrawerWidths.map(drawerWidths => drawerWidths.map(width => width > 0 ? bumperWidth : 0));

            //what is the minimum width the preceding cabinets require
            let precedingMinimumCabinetWidths = this.definition.cabinets
              .map((cabinetState, index) => { return { state: cabinetState, index: index }; })
              .filter((cabinetStateAndIndex) => cabinetStateAndIndex.index < cabinetIndex)
              .map(cabinetStateAndIndex => this.openDrawerCount(cabinetStateAndIndex.index) * bumperWidth);

            let deltaWidth = totalAvailableWidth - precedingWidth - successiveMinimumWidth;//the total amount by which to close the preceding cabinet drawers (number is negative)

            precedingCabinetDrawerWidths =
              precedingCabinetDrawerWidths.map((drawerWidths, cIndex) => {
                let cabinetWidth = Math.max(...drawerWidths);
                //cabinet will be reduced based on weight average of total reduction required
                let cabinetDeltaWidth = Math.round((cabinetWidth / precedingWidth) * deltaWidth);
                let newDrawerWidths = slideCabinetDrawers(drawerWidths, cabinetDeltaWidth, precedingMinimumCabinetWidths[cIndex]);
                return newDrawerWidths;
              });

          } else {//we need to close successive cabinet drawers only
            let deltaWidth = totalAvailableWidth - precedingWidth - successiveWidth;//the total amount by which to close the successive cabinet drawers

            successiveCabinetDrawerWidths =
              successiveCabinetDrawerWidths.map((drawerWidths, cIndex) => {
                let cabinetWidth = Math.max(...drawerWidths);
                //cabinet will be reduced based on weight average of total reduction required
                let cabinetDeltaWidth = Math.round((cabinetWidth / successiveWidth) * deltaWidth);
                let newDrawerWidths = slideCabinetDrawers(drawerWidths, cabinetDeltaWidth, successiveMinimumCabinetWidths[cIndex]);
                return newDrawerWidths;
              });
          }
        }

        let cabinetDrawerWidths = precedingCabinetDrawerWidths.concat([drawerWidths].concat(successiveCabinetDrawerWidths));

        //update the state object
        cabinetDrawerWidths.forEach((drawerWidths, cabinetIndex) => {
          let cabinetState = this.definition.cabinets[cabinetIndex];
          let drawerStates = cabinetState.drawers;
          drawerWidths.forEach((drawerWidth, drawerIndex) => {
            drawerStates[drawerIndex].width = drawerWidth;
          });
          cabinetState.width = Math.max(...drawerWidths);
        });

        //draw
        window.requestAnimationFrame((time) => this.draw());

        return this.getDrawer(cabinetIndex, drawerIndex);
      }
    }
    return undefined;
  }

  private draw() {
    this.definition.cabinets.forEach(cabinetState => {
      let cabinet = this.cabinets.get(cabinetState.name);
      cabinetState.drawers.forEach(drawerState => {
        let drawer = cabinet.getDrawer(drawerState.name);
        drawer.setWidth(drawerState.width, false);
        drawer.setResizable(drawerState.width > 0);
      });
      cabinet.setWidth(cabinetState.width);
    });
    this.getPanel().setHeight(this.definition.height);
  }

  protected disableSectionSelector(section: string) {
    let sectionSelector = (<HTMLElement>this.getContainerNode().querySelector(`div.cab0record div.tlmd-header.tlmd-selectors  a.tlmd-action-section[data-section="${section}"]`));
    if (sectionSelector) {
      sectionSelector.style.display = 'none';
      (<HTMLElement>sectionSelector.nextElementSibling).style.display = 'none';
    }
  }

  public selectSection = <(cabinet: string, drawer: string, section: string) => void>throttle((cabinet: string, drawer: string, section: string) => {
    let drawerElem: HTMLDivElement = this.getContainerNode().querySelector(`div.${cabinet}${drawer}`);
    drawerElem.querySelectorAll(`div.tlmd-header.tlmd-selectors  a`)
      .forEach((sectionSelectorElem: HTMLAnchorElement) => {
        let sectionName = sectionSelectorElem.dataset.section;
        if (sectionName === section) {
          sectionSelectorElem.classList.add('tlmd-selected');
        } else {
          sectionSelectorElem.classList.remove('tlmd-selected');
        }
      });

    drawerElem.querySelectorAll(`div.tlmd-sections > div`)
      .forEach((sectionSelectorElem: HTMLDivElement) => {
        let sectionName = sectionSelectorElem.dataset.name;
        if (sectionName === section) {
          if (sectionSelectorElem.classList.contains('tlmd-selected')) {
            //console.log(`Section ${sectionName} stays selected.`)
          } else {
            sectionSelectorElem.classList.add('tlmd-selected');
            //console.log(`Section ${sectionName} got selected.`)
          }
        } else {
          if (sectionSelectorElem.classList.contains('tlmd-selected')) {
            sectionSelectorElem.classList.remove('tlmd-selected');
            //console.log(`Section ${sectionName} got de-selected.`)
          } else {
            //console.log(`Section ${sectionName} stays de-selected.`)
          }
        }
      });
  }, 250);


  private getMonoDatumSectionAlternative(): string {
    let firstMultiDataSectionSelector: HTMLAnchorElement = this.getContainerNode().querySelector('div.cab0record div.tlmd-header.tlmd-selectors a.tlmd-data-section');
    if (firstMultiDataSectionSelector) {
      return firstMultiDataSectionSelector.dataset['section'];
    } else {
      return 'info'
    }
  }

  private isMonoDatumSectionSelected(): boolean {
    return this.getContainerNode().querySelector('div.cab0record div.tlmd-header.tlmd-selectors  a.tlmd-selected.tlmd-datum-section') != null;
  }
  private isMultiDatumSectionSelected(): boolean {
    return this.getContainerNode().querySelector('div.cab0record div.tlmd-header.tlmd-selectors  a.tlmd-selected.tlmd-data-section') != null;
  }

  public setSelectedData(dataIds: string[]) {
    let selectedDataIds = dataIds ? dataIds : [];
    let selectionSize = selectedDataIds.length;
    if (selectionSize <= 0) {
      this.getContainerNode().classList.remove('tlmd-data-selected', 'tlmd-datum-selected');
      if (this.isMonoDatumSectionSelected() || this.isMultiDatumSectionSelected()) {
        //no data selected but a data/datum section is selected so select section 'info'
        this.selectSection('cab0', 'record', 'info');
      }
    } else if (selectionSize === 1) {
      this.getContainerNode().classList.remove('tlmd-data-selected');
      this.getContainerNode().classList.add('tlmd-datum-selected');
      //currently (20210409) all sections should be selectable now

    } else {
      this.getContainerNode().classList.remove('tlmd-datum-selected');
      this.getContainerNode().classList.add('tlmd-data-selected');
      //when a a mono datum section has been selected, auto select the first multi datum section 
      if (this.isMonoDatumSectionSelected()) {
        let alternativeSection = this.getMonoDatumSectionAlternative();
        this.selectSection('cab0', 'record', alternativeSection);
      }
    }
    this.handleSelectedData(selectedDataIds);
  }

  public switchToEditSection(): void {
    this.getContainerNode().classList.remove('tlmd-data-selected');
    this.getContainerNode().classList.add('tlmd-datum-selected');
    this.selectSection('cab0', 'record', 'details');
  }

  protected panMapBounds(geometry: SpatialBounds): void {
    if (geometry) {
      if (util.isArea(geometry)) {
        if (this.getDataSource().getMap().getBounds().visible.inside(geometry)) {
          // all data visible -> zoom in
          this.getDataSource().getMap().getGMap().fitBounds(new google.maps.LatLngBounds(getSouthWest(geometry), getNorthEast(geometry)));
        } else {
          // not all data visible -> zoom out
          let unionBounds = this.getDataSource().getMap().getBounds().visible.union(geometry);
          this.getDataSource().getMap().getGMap().fitBounds(new google.maps.LatLngBounds(unionBounds.getSouthWest(), unionBounds.getNorthEast()));
        }
      } else {
        let mapSettings = this.getDataSource().getMap().getMapSettings();
        if (closeNumber(mapSettings.centre.lat, geometry.north) && closeNumber(mapSettings.centre.lng, geometry.east)) {
          mapSettings.zoom = Math.max(mapSettings.zoom, 17);
        } else {
          mapSettings.centre.lat = geometry.north;
          mapSettings.centre.lng = geometry.east;
        }
        this.getDataSource().getMap().setMapSettings(mapSettings);
      }
    }
  }

  protected panTimelineRange(temporalRange: TemporalRange): void {

    if (temporalRange && !util.isPerpetual(temporalRange)) {
      //do something when we do have coordinates
      let timeRange = this.getDataSource().getTimeline().getTimeRange().visible;
      if (util.duration(temporalRange) === 0) {
        //a moment rather then a period
        if (!(temporalRange.end > (timeRange.start + timeRange.mspp) && temporalRange.start < (timeRange.end - timeRange.mspp))) {
          //only re-center the timeline when the given coordinate is not on the timeline
          let width = timeRange.end - timeRange.start;
          timeRange.start = temporalRange.start - Math.round(width / 2);
          timeRange.end = timeRange.start + width;
        }
      } else if (util.isSince(temporalRange)) {
        //a period that started but has no defined end                                
        if (temporalRange.start >= (timeRange.end - timeRange.mspp)) {
          timeRange.start = temporalRange.start;
        }
      } else if (util.isUntil(temporalRange)) {
        //a period that ended but has no defined start                                
        if (temporalRange.end <= (timeRange.start + timeRange.mspp)) {
          timeRange.end = temporalRange.end;
        }
      } else {
        //period with a defined start and end
        if (temporalRange.start >= (timeRange.end - timeRange.mspp) || temporalRange.end <= (timeRange.start + timeRange.mspp)) {
          //period is not on the timeline
          timeRange.start = temporalRange.start - timeRange.mspp;
          timeRange.end = temporalRange.end + timeRange.mspp;
        } else {
          if (temporalRange.start >= (timeRange.start - timeRange.mspp) &&
            temporalRange.start < (timeRange.end - timeRange.mspp) &&
            temporalRange.end >= (timeRange.start + timeRange.mspp) && temporalRange.end <= (timeRange.end + timeRange.mspp)) {
            //the period is already included, now go for a tight fit
            timeRange.start = temporalRange.start - timeRange.mspp;
            timeRange.end = temporalRange.end + timeRange.mspp;
          } else {
            //pan the timeline as minimal as possible just to include the period
            if (temporalRange.start < (timeRange.start - timeRange.mspp)) {
              timeRange.start = temporalRange.start - timeRange.mspp;
            }
            if (temporalRange.end > (timeRange.end + timeRange.mspp)) {
              timeRange.end = temporalRange.end + timeRange.mspp;
            }
          }
        }
      }
      this.getDataSource().getTimeline().setRange(timeRange.start, timeRange.end);
    }
  }


  /**
   * Override this method to handle a new selected dataItem. When the
   * given dataItem is null/undefined then no dataItem is selected. 
   */
  protected handleSelectedData(_selectedDataIds: string[]) {
  }

  /**
   * Override this method when a data source is present. 
   */
  public getDataSource(): DataSource {
    return undefined;
  }
  /**
   * Override this method when a data source is present. 
   */
  public getDataList(): DataList {
    return undefined;
  }

  public panMap(geometry?: SpatialBounds): void {
    if (geometry) {
      this.panMapBounds(geometry);
    } else {
      let dataList = this.getDataList();
      if (dataList.getSize() > 0) {
        if (this.getDataSource().isSpatial(dataList.get(0))) {
          if (dataList.getSelectionSize() === 0) {
            this.panMapBounds(dataList.getBounds());
          } else {
            this.panMapBounds(dataList.getSelectionBounds());
          }
        }
      }
    }
  }

  public panTimeline(temporalRange?: TemporalRange): void {
    if (temporalRange) {
      this.panTimelineRange(temporalRange);
    } else {
      let dataList = this.getDataList();
      if (dataList.getSize() > 0) {
        if (this.getDataSource().isTemporal(dataList.get(0))) {
          if (dataList.getSelectionSize() === 0) {
            this.panTimelineRange(dataList.getRange());
          } else {
            this.panTimelineRange(dataList.getSelectionRange());
          }
        }
      }
    }
  }

  /**
    * When a datum gets deleted we want to autoselect a new datum. In this implementation
    * the natural order will determine the next  datum to be selected.
    */
  public getAutoSelectOnDeletionId(selectionIds: string[] | string): string {
    if (selectionIds && ((<string>selectionIds).substring || ((<string[]>selectionIds).push && selectionIds.length == 1)) && this.getDataList()) {
      let id = (<string>selectionIds).substring ? <string>selectionIds : (<string[]>selectionIds)[0];
      let index = this.getDataList().indexOf(id);
      if (index >= -1) {
        if (index < this.getDataList().getSize() - 1) {
          return this.getDataList().get(index + 1).id;
        } else {
          return index > 0 ? this.getDataList().get(index - 1).id : undefined;
        }
      }
    }
    return undefined;
  }

  /**
   * Override this method to handle a request for exporting the data managed by this container. 
   */
  public export(): void {
  }
  /**
   * Override this method to handle a request for viewer activation/de-activation. 
   */
  public enableVisualizer(_visualizerKey: string, _enable: boolean): void {
  }

  /**
   * Override this method to do special processing when we just came into view. 
   */
  public onFocus(): void {

  }
  /**
   * Override this method to do special processing when we just go out of view. 
   */
  public onBlur(): void {

  }

}
